import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FormInfo } from '@tabservice/tabfile-client';
import { formsQuery } from './queries';

/**
 * One tax year for the whole app. It sits in the header bar, it survives a
 * reload, and every screen that is year specific reads it from here rather than
 * keeping a copy of its own.
 */
export const TAX_YEAR_STORAGE_KEY = 'tabfile.taxYear';

/** Used until /v1/forms answers, and if the customer's forms carry no years. */
export const FALLBACK_TAX_YEARS = [2026, 2025];

/**
 * One store for the whole page, held on the window rather than in this module: the
 * development server can end up with two copies of a module after hot reloads, and a header
 * writing to one copy while the pages read the other is exactly "I changed the year and
 * nothing followed until I reloaded". `stored` undefined means "not read from storage yet",
 * null means "nothing stored".
 */
interface TaxYearStore {
  stored: number | null | undefined;
  /** The year an e-file in progress holds the app to, or null. See lockTaxYear. */
  locked?: number | null;
  listeners: Set<() => void>;
}
const store: TaxYearStore = ((globalThis as { __tabServiceTaxYear?: TaxYearStore }).__tabServiceTaxYear ??= {
  stored: undefined,
  locked: null,
  listeners: new Set<() => void>(),
});

function subscribe(listener: () => void): () => void {
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

function emit(): void {
  for (const listener of store.listeners) listener();
}

/** The year the browser remembers, or null when there is nothing usable. */
export function readStoredTaxYear(): number | null {
  if (store.stored !== undefined) return store.stored;
  try {
    const raw = localStorage.getItem(TAX_YEAR_STORAGE_KEY);
    const parsed = raw === null ? Number.NaN : Number(raw);
    store.stored = Number.isInteger(parsed) ? parsed : null;
  } catch {
    // Private browsing can refuse storage. The app still works, it just forgets.
    store.stored = null;
  }
  return store.stored;
}

export function writeStoredTaxYear(year: number): void {
  store.stored = year;
  try {
    localStorage.setItem(TAX_YEAR_STORAGE_KEY, String(year));
  } catch {
    // Nothing to do. The choice lives for this session only.
  }
  emit();
}

/**
 * Holds the app to one year while an e-file whose file is already on Tab Service is open.
 *
 * That file was checked against the year its draft was made for, so a header changed
 * afterwards only made the page and the check disagree: QA picked a file under 2025,
 * switched the header to 2026, and the next step still said the file did not match the
 * selected tax year (2025). While locked the header shows the e-file's year and cannot be
 * changed; the remembered choice is left alone and comes back once the lock is released.
 */
export function lockTaxYear(year: number | null): void {
  if ((store.locked ?? null) === year) return;
  store.locked = year;
  emit();
}

export function readLockedTaxYear(): number | null {
  return store.locked ?? null;
}

/** Drops the cached read. Tests use it; nothing in the app does. */
export function resetTaxYearCache(): void {
  store.stored = undefined;
  store.locked = null;
  emit();
}

/** Every year any of the customer's forms supports, newest first. */
export function taxYearsFromForms(forms: FormInfo[] | undefined): number[] {
  const seen = new Set<number>();
  for (const form of forms ?? []) {
    for (const year of form.taxYears ?? []) seen.add(year);
  }
  const years = [...seen].sort((left, right) => right - left);
  return years.length > 0 ? years : FALLBACK_TAX_YEARS;
}

/**
 * The year to show. A remembered year wins as long as the customer's forms
 * still cover it; otherwise we fall back to the newest year they can file.
 */
export function resolveTaxYear(storedYear: number | null, years: number[]): number {
  if (storedYear !== null && years.includes(storedYear)) return storedYear;
  return years[0] ?? (FALLBACK_TAX_YEARS[0] as number);
}

export interface TaxYearState {
  taxYear: number;
  setTaxYear: (year: number) => void;
  years: number[];
  /** True while an e-file in progress holds the year; the header cannot change it. */
  locked: boolean;
}

export function useTaxYear(): TaxYearState {
  const { data } = useQuery(formsQuery());
  const storedYear = useSyncExternalStore(
    subscribe,
    readStoredTaxYear,
    () => null,
  );
  const lockedYear = useSyncExternalStore(subscribe, readLockedTaxYear, () => null);
  const years = useMemo(() => taxYearsFromForms(data?.forms), [data]);
  const setTaxYear = useCallback((year: number) => writeStoredTaxYear(year), []);
  if (lockedYear !== null) return { taxYear: lockedYear, setTaxYear, years, locked: true };
  return { taxYear: resolveTaxYear(storedYear, years), setTaxYear, years, locked: false };
}
